import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Download, ArrowLeft, Plus, X, ShieldAlert, RefreshCw, FileDown, Clock, CheckCircle2, XCircle, Ban } from "lucide-react";

const DATASETS = ["businesses", "branches", "users", "customers", "pets", "bookings", "services",
  "invoices", "payments", "refunds", "staff", "inventory", "inventory_movements", "grooming"];
const FORMATS = ["csv", "json", "zip", "xlsx"];
const STATUS_STYLE = {
  QUEUED: "text-slate-300 bg-slate-700/40", RUNNING: "text-indigo-300 bg-indigo-500/15",
  COMPLETED: "text-emerald-300 bg-emerald-500/15", FAILED: "text-rose-300 bg-rose-500/15",
  CANCELLED: "text-amber-300 bg-amber-500/15", EXPIRED: "text-slate-400 bg-slate-800",
};
const StatusIcon = ({ s }) => s === "COMPLETED" ? <CheckCircle2 className="h-3.5 w-3.5" /> :
  s === "FAILED" ? <XCircle className="h-3.5 w-3.5" /> : s === "CANCELLED" ? <Ban className="h-3.5 w-3.5" /> :
  s === "RUNNING" ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />;

export default function ExportCenter() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([sp.get("dataset") || "customers"]);
  const [format, setFormat] = useState("zip");
  const [includePii, setIncludePii] = useState(false);
  const [tenantId, setTenantId] = useState(sp.get("tenant_id") || "");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/dataexport/jobs").then((r) => setJobs(r.data.jobs)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);

  const toggle = (d) => setSel((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d]);

  const start = async () => {
    if (!sel.length) return toast.error("Select at least one dataset");
    setBusy(true);
    try {
      await api.post("/platform/dataexport/jobs", { datasets: sel, format, include_pii: includePii, tenant_id: tenantId || "" });
      toast.success("Export started"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const cancel = async (jid) => { try { await api.post(`/platform/dataexport/jobs/${jid}/cancel`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const download = async (jid) => {
    try {
      const r = await api.get(`/platform/dataexport/jobs/${jid}/download`, { responseType: "blob" });
      const ct = r.headers["content-type"] || "";
      if (ct.includes("application/json")) {
        const txt = JSON.parse(await r.data.text());
        if (txt.download_url) return window.open(txt.download_url, "_blank");
      }
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = (jobs.find((j) => j.job_id === jid)?.files_created?.[0]) || "export"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="ec-center" className="text-slate-100">
      <button onClick={() => navigate("/platform-admin")} className="mb-3 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="h-4 w-4" /> Platform</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-indigo-500/15 grid place-items-center"><Download className="h-5 w-5 text-indigo-400" /></div>
          <div><h1 className="font-display text-2xl font-bold">Data Exports</h1>
            <p className="text-xs text-slate-500">Asynchronous, streamed exports · private storage · expiring links · Preview</p></div>
        </div>
        <button data-testid="ec-new" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-indigo-400"><Plus className="h-4 w-4" /> New export</button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-800" data-testid="ec-jobs">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500">
            <tr>{["Job", "Datasets", "Format", "Status", "Progress", "Rows", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job_id} data-testid={`ec-job-${j.job_id}`} className="border-t border-slate-800">
                <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{j.job_id.slice(0, 14)}…{j.include_pii && <span className="ml-1 rounded bg-rose-500/15 px-1 text-[10px] text-rose-300">PII</span>}</td>
                <td className="px-3 py-2.5 text-slate-300">{j.datasets.slice(0, 3).join(", ")}{j.datasets.length > 3 ? ` +${j.datasets.length - 3}` : ""}</td>
                <td className="px-3 py-2.5 uppercase text-slate-400">{j.format}</td>
                <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[j.status] || ""}`}><StatusIcon s={j.status} />{j.status}</span></td>
                <td className="px-3 py-2.5 w-40">
                  <div className="h-1.5 w-full rounded-full bg-slate-800"><div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${j.progress_percent || 0}%` }} /></div>
                  <span className="text-[11px] text-slate-500">{j.progress_percent || 0}%</span>
                </td>
                <td className="px-3 py-2.5">{(j.rows_processed || 0).toLocaleString("en-IN")}{j.error_message && <div className="text-[11px] text-rose-400">{j.error_message}</div>}</td>
                <td className="px-3 py-2.5 text-right">
                  {j.status === "COMPLETED" && <button data-testid={`ec-dl-${j.job_id}`} onClick={() => download(j.job_id)} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-xs hover:bg-slate-800"><FileDown className="h-3.5 w-3.5" /> Download</button>}
                  {["QUEUED", "RUNNING"].includes(j.status) && <button data-testid={`ec-cancel-${j.job_id}`} onClick={() => cancel(j.job_id)} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-amber-300 hover:bg-slate-800">Cancel</button>}
                </td>
              </tr>
            ))}
            {!jobs.length && <tr><td colSpan={7} data-testid="ec-empty" className="px-3 py-10 text-center text-slate-500">No exports yet</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="ec-modal">
          <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold">New export</h2><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Datasets</label>
              <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
                {DATASETS.map((d) => (
                  <label key={d} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-800/50">
                    <input type="checkbox" data-testid={`ec-ds-${d}`} checked={sel.includes(d)} onChange={() => toggle(d)} className="accent-indigo-500" />
                    <span className={sel.includes(d) ? "text-slate-100" : "text-slate-400"}>{d}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div><label className="text-xs text-slate-500">Format</label>
                <select data-testid="ec-format" value={format} onChange={(e) => setFormat(e.target.value)} className="mt-1 block rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
                  {FORMATS.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select></div>
              <div className="flex-1"><label className="text-xs text-slate-500">Tenant filter (optional)</label>
                <input data-testid="ec-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="tenant/business ID" className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm" /></div>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="ec-pii" checked={includePii} onChange={(e) => setIncludePii(e.target.checked)} className="accent-rose-500" /> Include personally identifiable information (PII)</label>
            {includePii && <div data-testid="ec-pii-warning" className="mt-2 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> This export contains personally identifiable information and will be audited.</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="ec-start" disabled={busy} onClick={start} className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-indigo-400 disabled:opacity-50">{busy ? "Starting…" : "Start export"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
